/**
 * Shared WebAudio analyser → music bands.
 * Port of freemusiers-games MusicEngine (player band math, simplified).
 */

export type MusicBands = {
  kick: number;
  bass: number;
  mids: number;
  highs: number;
  energy: number;
  pulse: number;
  beat: number;
};

const EMPTY: MusicBands = {
  kick: 0,
  bass: 0,
  mids: 0,
  highs: 0,
  energy: 0,
  pulse: 0,
  beat: 0,
};

export class MusicEngine {
  readonly audio: HTMLAudioElement;
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaElementAudioSourceNode | null = null;
  private dest: MediaStreamAudioDestinationNode | null = null;
  /** Monitoring volume only — record path stays full-scale. */
  private monitorGain: GainNode | null = null;
  private freq = new Uint8Array(0);
  private time = new Uint8Array(0);
  private waveNorm = new Float32Array(0);
  private smooth = { kick: 0, bass: 0, mids: 0, highs: 0, energy: 0 };
  private peak = { kick: 0.14, bass: 0.14, mids: 0.14, highs: 0.14, energy: 0.14 };
  private lastEnergy = 0;
  private bands: MusicBands = { ...EMPTY };
  private unlocked = false;
  private objectUrl: string | null = null;
  /** 0.5–2.0 band boost for visualizers. */
  sensitivity = 1;

  constructor() {
    this.audio = new Audio();
    this.audio.crossOrigin = 'anonymous';
    this.audio.preload = 'auto';
    this.audio.setAttribute('playsinline', 'true');
    this.audio.setAttribute('webkit-playsinline', 'true');
  }

  get current(): MusicBands {
    return this.bands;
  }

  get waveform(): Float32Array {
    return this.waveNorm;
  }

  get frequency(): Uint8Array {
    return this.freq;
  }

  get isPlaying(): boolean {
    return !this.audio.paused && !!this.audio.src;
  }

  get duration(): number {
    return Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
  }

  get currentTime(): number {
    return this.audio.currentTime || 0;
  }

  /** Mixed audio stream for MediaRecorder (full level, independent of monitor gain). */
  getAudioStream(): MediaStream | null {
    return this.dest?.stream ?? null;
  }

  async unlock(): Promise<void> {
    if (this.unlocked && this.ctx?.state === 'running') return;
    if (!this.ctx) {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AC();
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 512;
      this.analyser.smoothingTimeConstant = 0.72;
      this.freq = new Uint8Array(this.analyser.frequencyBinCount);
      this.time = new Uint8Array(this.analyser.fftSize);
      this.waveNorm = new Float32Array(this.analyser.fftSize);
      this.source = this.ctx.createMediaElementSource(this.audio);
      this.dest = this.ctx.createMediaStreamDestination();
      this.monitorGain = this.ctx.createGain();
      this.monitorGain.gain.value = 1;

      this.source.connect(this.analyser);
      // Full-level feed for recording
      this.analyser.connect(this.dest);
      // Monitor path (volume slider)
      this.analyser.connect(this.monitorGain);
      this.monitorGain.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
    this.unlocked = true;
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  async loadUrl(url: string): Promise<void> {
    await this.unlock();
    this.audio.pause();
    this.revokeObjectUrl();
    this.audio.src = url;
    this.audio.load();
    await this.waitCanPlay();
  }

  async loadFile(file: File): Promise<void> {
    await this.unlock();
    this.audio.pause();
    this.revokeObjectUrl();
    this.objectUrl = URL.createObjectURL(file);
    this.audio.src = this.objectUrl;
    this.audio.load();
    await this.waitCanPlay();
  }

  private waitCanPlay(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const ok = () => {
        cleanup();
        resolve();
      };
      const bad = () => {
        cleanup();
        reject(new Error('Audio failed to load'));
      };
      const cleanup = () => {
        this.audio.removeEventListener('canplay', ok);
        this.audio.removeEventListener('error', bad);
      };
      if (this.audio.readyState >= 2) {
        resolve();
        return;
      }
      this.audio.addEventListener('canplay', ok, { once: true });
      this.audio.addEventListener('error', bad, { once: true });
    });
  }

  async play(): Promise<void> {
    await this.unlock();
    await this.audio.play();
  }

  pause(): void {
    this.audio.pause();
  }

  seek(t: number): void {
    if (!Number.isFinite(t)) return;
    const d = this.duration;
    const clamped = d > 0 ? Math.max(0, Math.min(d, t)) : Math.max(0, t);
    this.audio.currentTime = clamped;
  }

  /** Monitor volume 0–1 (does not affect recorded level). */
  setVolume(v: number): void {
    const g = Math.max(0, Math.min(1, v));
    this.audio.volume = 1;
    if (this.monitorGain) this.monitorGain.gain.value = g;
  }

  setSensitivity(s: number): void {
    this.sensitivity = Math.max(0.4, Math.min(2.2, s));
  }

  /** Call once per frame. */
  tick(): MusicBands {
    if (!this.analyser || this.audio.paused || !this.audio.src) {
      this.bands = {
        kick: this.smooth.kick * 0.9,
        bass: this.smooth.bass * 0.9,
        mids: this.smooth.mids * 0.9,
        highs: this.smooth.highs * 0.9,
        energy: this.smooth.energy * 0.9,
        pulse: 0,
        beat: 0,
      };
      this.waveNorm.fill(0);
      return this.bands;
    }

    this.analyser.getByteFrequencyData(this.freq);
    this.analyser.getByteTimeDomainData(this.time);
    for (let i = 0; i < this.time.length; i++) {
      this.waveNorm[i] = (this.time[i] - 128) / 128;
    }

    const n = this.freq.length;
    const avg = (from: number, to: number) => {
      let s = 0;
      let c = 0;
      const a = Math.max(0, Math.floor(from));
      const b = Math.min(n, Math.ceil(to));
      for (let i = a; i < b; i++) {
        s += this.freq[i];
        c++;
      }
      return c ? s / c / 255 : 0;
    };

    const kickRaw = avg(0, n * 0.04);
    const bassRaw = avg(n * 0.04, n * 0.12);
    const midsRaw = avg(n * 0.12, n * 0.4);
    const highsRaw = avg(n * 0.4, n * 0.9);
    const energyRaw = kickRaw * 0.34 + bassRaw * 0.3 + midsRaw * 0.22 + highsRaw * 0.14;
    const curve = (x: number) => Math.pow(Math.min(1, Math.max(0, x)), 0.85);

    const smoothBand = (
      key: keyof typeof this.smooth,
      target: number,
      up = 0.28,
      down = 0.1,
    ) => {
      const prev = this.smooth[key];
      const next = target > prev ? prev + (target - prev) * up : prev + (target - prev) * down;
      this.smooth[key] = next;
      this.peak[key] = Math.max(this.peak[key] * 0.995, next, 0.12);
      return Math.min(1, next / this.peak[key]);
    };

    const boost = this.sensitivity;
    const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

    const kick = clamp01(smoothBand('kick', curve(kickRaw), 0.35, 0.12) * boost);
    const bass = clamp01(smoothBand('bass', curve(bassRaw), 0.3, 0.1) * boost);
    const mids = clamp01(smoothBand('mids', curve(midsRaw), 0.25, 0.1) * boost);
    const highs = clamp01(smoothBand('highs', curve(highsRaw), 0.22, 0.1) * boost);
    const energy = clamp01(smoothBand('energy', curve(energyRaw), 0.22, 0.09) * boost);

    const pulse = Math.max(0, energy - this.lastEnergy) * 4;
    this.lastEnergy = energy;
    const beat = pulse > 0.12 || (kick > 0.72 && pulse > 0.04) ? Math.min(1, pulse + kick * 0.3) : 0;

    this.bands = { kick, bass, mids, highs, energy, pulse: Math.min(1, pulse), beat };
    return this.bands;
  }

  dispose(): void {
    this.audio.pause();
    this.revokeObjectUrl();
    this.audio.removeAttribute('src');
    void this.ctx?.close();
    this.ctx = null;
    this.analyser = null;
    this.source = null;
    this.dest = null;
    this.monitorGain = null;
  }
}
